import React from 'react';

/**
 * Transforme tous les liens dans un texte en liens HTML cliquables avec target="_blank"
 * @param {string} text - Le texte contenant potentiellement des URLs
 * @returns {string} - Le texte avec les URLs transformées en liens HTML
 */
export const linkifyText = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Expression régulière pour détecter les URLs (optimisée pour localhost et urls courantes)
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+|www\.[^\s<>"{}|\\^`\[\]]+|localhost(?::[0-9]{1,5})?(?:\/[^\s<>"{}|\\^`\[\]]*)?|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?::[0-9]{1,5})?(?:\/[^\s<>"{}|\\^`\[\]]*)?|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.(?:com|org|net|edu|gov|mil|int|eu|fr|uk|de|jp|cn|ru|br|au|ca|in|it|nl|es|se|no|dk|fi|pl|be|ch|at|cz|hu|pt|gr|ie|bg|hr|sk|si|lt|lv|ee|lu|mt|cy|is|li|mc|sm|va|ad|md|ua|by|rs|me|mk|al|ba|xk|tr|il|ae|sa|eg|ma|tn|dz|ly|sd|et|ke|tz|ug|rw|zm|zw|bw|na|sz|ls|mw|mg|mu|sc|km|dj|so|er|cf|td|cm|gq|ga|cg|cd|ao|st|gw|gn|sl|lr|ci|gh|tg|bj|ne|ng|ml|bf|sn|gm|cv|mr|eh|sd)(?::[0-9]{1,5})?(?:\/[^\s<>"{}|\\^`\[\]]*)?)/gi;

  return text.replace(urlRegex, (match) => {
    // Ajouter http:// si l'URL commence par www.
    let href = match;
    if (match.startsWith('www.')) {
      href = 'http://' + match;
    }

    // S'assurer que l'URL est valide
    try {
      new URL(href);
    } catch (e) {
      // Si l'URL n'est pas valide, retourner le texte original
      return match;
    }

    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${match}</a>`;
  });
};

/**
 * Version React qui retourne du JSX au lieu de HTML string
 * @param {string} text - Le texte contenant potentiellement des URLs
 * @returns {Array} - Un tableau d'éléments React (strings et JSX)
 */
export const linkifyTextReact = (text) => {
  if (!text || typeof text !== 'string') {
    return [text];
  }

  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+|www\.[^\s<>"{}|\\^`\[\]]+|localhost(?::[0-9]{1,5})?(?:\/[^\s<>"{}|\\^`\[\]]*)?|(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(?::[0-9]{1,5})?(?:\/[^\s<>"{}|\\^`\[\]]*)?|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.(?:com|org|net|edu|gov|mil|int|eu|fr|uk|de|jp|cn|ru|br|au|ca|in|it|nl|es|se|no|dk|fi|pl|be|ch|at|cz|hu|pt|gr|ie|bg|hr|sk|si|lt|lv|ee|lu|mt|cy|is|li|mc|sm|va|ad|md|ua|by|rs|me|mk|al|ba|xk|tr|il|ae|sa|eg|ma|tn|dz|ly|sd|et|ke|tz|ug|rw|zm|zw|bw|na|sz|ls|mw|mg|mu|sc|km|dj|so|er|cf|td|cm|gq|ga|cg|cd|ao|st|gw|gn|sl|lr|ci|gh|tg|bj|ne|ng|ml|bf|sn|gm|cv|mr|eh|sd)(?::[0-9]{1,5})?(?:\/[^\s<>"{}|\\^`\[\]]*)?)/gi;

  const parts = text.split(urlRegex);
  const result = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];

    if (urlRegex.test(part)) {
      // C'est une URL
      let href = part;
      if (part.startsWith('www.')) {
        href = 'http://' + part;
      }

      // Vérifier que l'URL est valide
      try {
        new URL(href);
        result.push(
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#007bff', textDecoration: 'underline' }}
          >
            {part}
          </a>
        );
      } catch (e) {
        // URL invalide, garder le texte original
        result.push(part);
      }
    } else if (part) {
      // Texte normal
      result.push(part);
    }
  }

  return result;
};

/**
 * Version simplifiée qui ne détecte que les URLs communes
 * @param {string} text - Le texte contenant potentiellement des URLs
 * @returns {string} - Le texte avec les URLs transformées en liens HTML
 */
export const linkifySimple = (text) => {
  if (!text || typeof text !== 'string') {
    return text;
  }

  // Expression régulière simplifiée pour les cas les plus courants (inclut localhost et ports)
  const simpleUrlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|localhost(?::[0-9]{1,5})?(?:\/[^\s]*)?|[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|fr|uk|de)(?:\/[^\s]*)?)/gi;

  return text.replace(simpleUrlRegex, (match) => {
    let href = match;
    if (match.startsWith('www.')) {
      href = 'http://' + match;
    }

    return `<a href="${href}" target="_blank" rel="noopener noreferrer">${match}</a>`;
  });
}; 